/* jshint esversion: 6 */

/**
 * HintEmptyContentValidator class is responsible for validating if a hint object has empty content.
 * It uses dependency injection to get necessary services and handles the validation logic.
 */
class HintEmptyContentValidator {
    /**
     * Constructor for HintEmptyContentValidator.
     * @param {Object} $injector - AngularJS injector service.
     * @param {Object} model - The model to be validated.
     */
    constructor($injector, model) {
        this.$injector = $injector;
        this.model = model;

        this._TaskDataService = null;
        this._translate = null;
        this._fetchContext = null;
        this._messageTranslateString = 'CMS.FACTORIES.MODELS.VALIDATORS.EMPTY.HINT';
        this._message = null;
        this._forced_check = false;
    }

    /**
     * Get the TaskDataService instance.
     * @returns {Object} TaskDataService instance.
     */
    getTaskDataService() {
        if (!this._TaskDataService) {
            this._TaskDataService = this.$injector.get('TaskDataService');
        }
        return this._TaskDataService;
    }

    /**
     * Get the $translate service instance.
     * @returns {Object} $translate service instance.
     */
    getTranslateService() {
        if (!this._translate) {
            this._translate = this.$injector.get('$translate');
        }
        return this._translate;
    }

    /**
     * Get the fetchContext instance.
     * @returns {Object} fetchContext instance.
     */
    getFetchContext() {
        if (!this._fetchContext) {
            this._fetchContext = this.getTaskDataService().fetchContext();
        }
        return this._fetchContext;
    }

    /**
     * Find the parent container of the given model.
     * @param {Object} model - The model to find the parent container for.
     * @returns {Object|null} The parent container or null if not found.
     */
    _getParentContainer(model) {
        const parents = model.getParents();
        let parent_container = null;
        const parent_containers = _.filter(parents, item => item.getType() === 'container');
        for (let container_key in parent_containers) {
            if (parent_containers.hasOwnProperty(container_key)) {
                const container = this.getFetchContext().read(parent_containers[container_key].getUUID());
                let objects;
                if (container.isShowTabs()) {
                    const sub_containers = container.getTabs();
                    for (let sub_container_key in sub_containers) {
                        if (sub_containers.hasOwnProperty(sub_container_key)) {
                            const sub_container = this.getFetchContext().read(sub_containers[sub_container_key].content);
                            objects = sub_container.getObjects();
                        }
                    }
                } else if (container.isShowCarousels()) {
                    const sub_containers = container.getCarousels();
                    for (let sub_container_key in sub_containers) {
                        if (sub_containers.hasOwnProperty(sub_container_key)) {
                            const sub_container = this.getFetchContext().read(sub_containers[sub_container_key].content);
                            objects = sub_container.getObjects();
                        }
                    }
                } else if (container.isShowIaaContainer()) {
                    const sub_containers = container.getIaaContainer();
                    for (let sub_container_key in sub_containers) {
                        if (sub_containers.hasOwnProperty(sub_container_key)) {
                            const sub_container = this.getFetchContext().read(sub_containers[sub_container_key].content);
                            objects = sub_container.getObjects();
                        }
                    }
                } else {
                    objects = container.getObjects();
                }

                if (objects && objects.indexOf(model.getUUID()) !== -1) {
                    parent_container = container;
                }
            }
        }

        return parent_container;
    }

    /**
     * Validate the model based on its type and content.
     * @param {string} from - The source from which the validation is called.
     * @returns {Object} The validation result.
     */
    validate(from) {
        let result = {};
        let error = false;

        if (this.model.getObjectType() === 'hint') {
            const parent_container = this._getParentContainer(this.model);

            if (parent_container && typeof parent_container.getObjects === 'function' && parent_container.getObjects() && parent_container.getObjects().length > 0) {
                const objects = parent_container.getObjects();

                error = true;
                for (let key in objects) {
                    if (objects.hasOwnProperty(key)) {
                        const uuid = objects[key];
                        if (!this._isValidUUID(uuid)) continue;

                        const content_object = this.getFetchContext().read(uuid);

                        if (content_object.getObjectType() !== 'hint') {
                            let preferences;
                            if (typeof content_object.getPreferences === 'function') {
                                preferences = content_object.getPreferences();
                            } else {
                                preferences = content_object.preferences;
                            }

                            if (preferences && preferences.targetHintObject === this.model.getUUID()) {
                                error = false;
                            }
                        }
                    }
                }
            }

            result = {
                'onlyBanner': false,
                'validator': this.getValidatorName(),
                'modelUUID': this.model.getUUID(),
                'error': error,
                'status': error ? 'DANGER' : 'SUCCESS', // WARNING | DANGER | SUCCESS
                'message': this.getErrorMessage(),
                'callbacks': [
                    'validationDisplayObjectHeader-' + this.model.getUUID()
                ]
            };

            return result;

        } else {
            const parent_container = this._getParentContainer(this.model);
            if (parent_container && typeof parent_container.getObjects === 'function' && parent_container.getObjects() && parent_container.getObjects().length > 0) {
                const objects = parent_container.getObjects();

                for (let key in objects) {
                    if (objects.hasOwnProperty(key)) {
                        const uuid = objects[key];
                        if (!this._isValidUUID(uuid)) continue;

                        const content_object = this.getFetchContext().read(uuid);

                        if (content_object.getObjectType() === 'hint') {
                            content_object.validate(from);
                        }
                    }
                }
            }

            return result;
        }
    }

    /**
     * Get the error message for the validation.
     * @returns {string} The error message.
     */
    getErrorMessage() {
        const questionHeadline = this.model.getQuestionHeadline();
        this._message = this.getTranslateService().instant(this._messageTranslateString, {
            s: questionHeadline ? questionHeadline : ''
        });

        return this._message;
    }

    /**
     * Check if the validation is forced.
     * @returns {boolean} True if the validation is forced, false otherwise.
     */
    isForcedCheck() {
        return this._forced_check;
    }

    /**
     * Get the name of the validator.
     * @returns {string} The name of the validator.
     */
    getValidatorName() {
        return 'HintEmptyContentValidator';
    }

    /**
     * Check if a given string is a valid UUID.
     * @param {string} uuid - The UUID to validate.
     * @returns {boolean} True if the UUID is valid, false otherwise.
     */
    _isValidUUID(uuid) {
        const regex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        return regex.test(uuid);
    }
}

export default HintEmptyContentValidator;